import { useCallback, useMemo } from 'react';

import { readContract, waitForTransactionReceipt } from '@wagmi/core';
import { Address } from 'viem';
import {
  useReadContract,
  useReadContracts,
  useBalance as useTokenBalance,
} from 'wagmi';

import { useAuth } from '@contexts/common/AuthorizationContext';

import { config } from '@tools/wagmi';

import { mapDataToFields } from '@helpers/etfs/common.helpers';

import { useWriteContractExtraGas } from '@utils/contract.utils';
import { applyDecimals, toBN } from '@utils/formaters.utils';
import { isAddressesEqual } from '@utils/tokens.utils';

import showToast from '@components/common/Toast';

import {
  CHAIN_ID,
  DEFAULT_CHAIN_ID,
  ZERO_ADDRESS,
} from '@constants/common/chain.constants';
import {
  ErrorMessages,
  SuccessMessages,
} from '@constants/common/messages.constants';
import { ToastType } from '@constants/common/toast.constants';
import { MAX_UINT256 } from '@constants/common/tokens.constants';

import abi from '@abi/common/ERC20Permit';

import { TokenERC20 } from '@app-types/common/token.types';

export const getERC20Contract = (baseAddress: Address, chainId: number) => ({
  address: baseAddress,
  abi,
  chainId,
});

export const getNonces = (
  address: Address,
  account: Address,
): Promise<bigint> =>
  readContract(config, {
    abi,
    address,
    functionName: 'nonces',
    args: [account],
  });

type ApproveParamType = {
  spender: Address | string;
  tokenAddress: Address | string;
  amount: string;
  decimals: number;
  chainId: CHAIN_ID;
  handleUpdate?: () => Promise<void>;
};

export const useApprove = ({
  spender,
  tokenAddress,
  amount,
  decimals = 18,
  chainId,
  handleUpdate,
}: ApproveParamType) => {
  const { account } = useAuth();

  const {
    data: allowance,
    refetch,
    isPending: isPendingAllowance,
  } = useReadContract({
    abi,
    address: tokenAddress as Address,
    functionName: 'allowance',
    chainId,
    args: [account as Address, spender as Address],
  });

  const { writeContract, isPending: isPendingApprove } =
    useWriteContractExtraGas({
      config,
      mutation: {
        onSuccess: async (data) => {
          const transactionReceipt = await waitForTransactionReceipt(config, {
            hash: data,
          });
          await refetch();
          if (handleUpdate) {
            await handleUpdate();
          }
          showToast(
            {
              title: SuccessMessages.Approved,
              withLink: {
                hash: transactionReceipt.transactionHash,
                chainId: DEFAULT_CHAIN_ID,
              },
            },
            ToastType.Success,
          );
        },
        onError: (e) => {
          console.log('Approve:', e);
          showToast(
            { title: ErrorMessages.DeniedTransaction },
            ToastType.Error,
          );
        },
      },
    });

  const isApprove = useMemo(
    () =>
      tokenAddress === ZERO_ADDRESS ||
      (allowance !== undefined &&
        toBN(allowance).gte(applyDecimals(amount || 0, decimals))) ||
      !account,
    [tokenAddress, allowance, amount, decimals, account],
  );

  const approve = useCallback(() => {
    writeContract({
      abi,
      address: tokenAddress as Address,
      functionName: 'approve',
      args: [spender as Address, MAX_UINT256],
      chainId,
    });
  }, [spender, tokenAddress, writeContract, chainId]);

  return {
    approve,
    isApprove,
    refetch,
    isPendingAllowance,
    isPendingApprove,
  };
};

type CheckAllowancesParamType = {
  spender: Address | string;
  tokenAddress: Address | string;
  amount: string;
  decimals: number;
  chainId: CHAIN_ID;
};

export const useCheckApproves = (params: CheckAllowancesParamType[]) => {
  const { account } = useAuth();

  const allowancesCall = useMemo(
    () =>
      params.map((item) => ({
        abi,
        address: item.tokenAddress as Address,
        functionName: 'allowance' as const,
        args: [account as Address, item.spender as Address],
        chainId: item.chainId,
      })),
    [params, account],
  );

  const { data, isPending, refetch } = useReadContracts({
    contracts: allowancesCall,
    allowFailure: false,
    query: {
      enabled: !!allowancesCall.length && !!account,
    },
  });

  const approvesData: Record<string, boolean> | null = useMemo(() => {
    if (!data) return null;

    return data.reduce((acc, item, index) => {
      const address = params[index].tokenAddress;
      const { amount } = params[index];
      const { decimals } = params[index];
      const value = applyDecimals(amount, decimals);

      return {
        ...acc,
        [address]: toBN(item).gte(value),
      };
    }, {});
  }, [data, params]);

  return {
    data: approvesData,
    isPending,
    refetch,
  };
};

type BalanceParamType = {
  tokenAddress?: Address;
  chainId?: CHAIN_ID;
  contractAddress?: Address;
};

export const useBalance = ({
  tokenAddress,
  chainId,
  contractAddress,
}: BalanceParamType) => {
  const { account } = useAuth();

  const { data, refetch, isPending } = useTokenBalance({
    config,
    address: contractAddress || account,
    token: tokenAddress === ZERO_ADDRESS ? undefined : tokenAddress,
    chainId,
    query: {
      enabled: !!tokenAddress,
    },
  });

  return {
    balance: toBN(data?.value || 0),
    refetch,
    isPending: isPending && !!account,
  };
};

const ERC20_FUNCTION_CONFIGS = [
  { functionName: 'symbol', field: 'symbol' },
  { functionName: 'name', field: 'name' },
  { functionName: 'decimals', field: 'decimals' },
] as const;

interface ERC20TokenInfo {
  address: Address;
  chainId: number;
}

export const useERC20Info = (tokens: ERC20TokenInfo[]) => {
  const tokensCalls = useMemo(() => {
    if (!tokens.length) return [];

    return tokens
      .map(({ address, chainId }) =>
        ERC20_FUNCTION_CONFIGS.map(({ functionName }) => ({
          ...getERC20Contract(address, chainId),
          functionName,
          chainId,
        })),
      )
      .flat();
  }, [tokens]);

  const { data, isPending } = useReadContracts({
    contracts: tokensCalls,
    allowFailure: false,
    query: {
      enabled: !!tokensCalls.length,
    },
  });

  const tokensInfo = useMemo(() => {
    if (!data?.length) return [];

    return tokens.map((token, index) => {
      const { length } = ERC20_FUNCTION_CONFIGS;
      const startIndex = index * length;
      const tokenData = data.slice(startIndex, startIndex + length);

      return mapDataToFields<TokenERC20>(tokenData, ERC20_FUNCTION_CONFIGS, {
        address: token.address,
      });
    });
  }, [data, tokens]);

  return {
    tokensInfo,
    isPending,
  };
};

export const useERC20Balances = (
  tokens: ERC20TokenInfo[],
  addressArgs?: Address,
) => {
  const { account } = useAuth();

  const addressArg = useMemo(
    () => addressArgs || account || ZERO_ADDRESS,
    [account, addressArgs],
  );

  const erc20BalanceCalls = useMemo(
    () =>
      tokens.map(({ address, chainId }) => ({
        ...getERC20Contract(address, chainId),
        functionName: 'balanceOf' as const,
        chainId,
        args: [addressArg],
      })),
    [tokens, addressArg],
  );

  const {
    data,
    isPending: isPendingBalances,
    refetch,
  } = useReadContracts({
    contracts: erc20BalanceCalls,
    allowFailure: false,
    query: {
      enabled: !!erc20BalanceCalls.length && !!addressArg,
    },
  });

  const balances = useMemo(
    () =>
      tokens.map((item, index) => {
        const balance =
          data && !isAddressesEqual(addressArg, ZERO_ADDRESS)
            ? toBN(data[index])
            : toBN(0);

        return {
          ...item,
          balance,
        };
      }),
    [addressArg, data, tokens],
  );

  const isPending = useMemo(
    () => !!tokens.length && isPendingBalances,
    [isPendingBalances, tokens],
  );

  return {
    balances,
    isPending,
    refetch,
  };
};
